import { styled } from '@mui/material';

import { ReactNode } from 'react';

import { Typography } from '../Typography';

const getBackground = (checked: boolean, compact?: boolean) => {
  if (compact) return 'transparent';

  return checked
    ? 'var(--background-background-color)'
    : 'var(--background-background-secondary-color)';
};

const StyledRadio = styled('div')<{ checked: boolean; compact?: boolean }>(
  ({ theme, checked, compact }) => ({
    padding: compact
      ? `${theme.spacing(1)} ${theme.spacing(0.5)}`
      : theme.spacing(3),
    borderRadius: theme.spacing(1),
    cursor: 'pointer',
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    border: compact ? 'none' : '1px solid var(--stroke-stroke-secondary-color)',
    backgroundColor: getBackground(checked, compact),
  })
);

const StyledCheckmark = styled('div')({
  width: 15,
  height: 15,
  flex: '0 0 auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid var(--stroke-stroke-secondary-color)',
  background: 'var(--background-background-color)',
  borderRadius: '50%',
  position: 'relative',
  zIndex: 1,
  overflow: 'auto',
  '&::after': {
    content: "''",
    display: 'inline-block',
    zIndex: 1,
    width: '5px',
    height: '5px',
    top: 0,
    left: 0,
    borderRadius: '50%',
    backgroundColor: 'var(--background-background-color)',
  },
});

const StyledInput = styled('input')({
  position: 'absolute',
  width: 0,
  height: 0,
  opacity: 0,
  cursor: 'pointer',
  '&:checked': {
    '+ div': {
      backgroundColor: 'var(--button-button-primary-color)',
      border: 'none',
      '&::after': {
        background: 'var(--background-background-color)',
      },
    },
  },
});

const Content = styled('div')({
  justifyContent: 'center',
  flexDirection: 'column',
  display: 'flex',
  flex: 1,
});

const Label = styled(Typography)({
  fontSize: 13,
  fontWeight: 500,
  color: 'var(--text-text-primary-color)',
});

const Description = styled(Typography)({
  fontSize: 13,
  fontWeight: 300,
  lineHeight: '17px',
  color: 'var(--text-text-secondary-color)',
  whiteSpace: 'pre-wrap',
});

export type RadioProps = {
  checked: boolean;
  name: string;
  value: string;
  label: ReactNode;
  description?: string;
  compact?: boolean;
  disabled?: boolean;
  right?: ReactNode;
  onChange: (value: string) => void;
};

export const Radio = ({
  checked,
  name,
  label,
  description,
  value,
  compact,
  right,
  disabled,
  onChange,
}: RadioProps) => {
  const handleChange = () => {
    onChange(value);
  };

  return (
    <StyledRadio checked={checked} onClick={handleChange} compact={compact}>
      <StyledInput
        name={name}
        value={value}
        type="radio"
        checked={checked}
        disabled={disabled}
        data-ph-capture-attribute-id={name}
        data-ph-capture-attribute-value={value}
      />
      <StyledCheckmark />
      <Content>
        <Label>{label}</Label>
        {description ? <Description>{description}</Description> : null}
      </Content>
      {right ? right : null}
    </StyledRadio>
  );
};
